// Wire the offline cache into our axios apiClient.
//
// Behaviour:
//  - On a successful GET, write-through to the response cache.
//  - On a GET network failure (no response, or status 0), fall back to the
//    cached response if we have one. The synthesized response is flagged
//    with `fromCache: true` so callers can render a "stale" badge.
//  - We deliberately do NOT touch non-GET requests here — those go through
//    the mutation queue in Step 4.
//  - We deliberately skip caching auth endpoints and anything explicitly
//    marked `config.skipOfflineCache = true`.

import { putResponse, getResponse, putEvents, putProfile, cacheKeyForUrl } from "./offlineCache";

const isGet = (config) => (config?.method || "get").toLowerCase() === "get";

const isCacheable = (config) => {
  if (!config) return false;
  if (config.skipOfflineCache) return false;
  const url = config.url || "";
  // Auth and OAuth flows must not be cached — they're state-changing or
  // session-bound, and serving a stale /auth/me would lie about who's logged in.
  if (/^\/auth\//.test(url)) return false;
  if (/^\/calendar\/auth-url/.test(url)) return false;
  // Binary downloads (ical/csv/pdf) — skip; they're large and not used offline.
  if (config.responseType === "blob" || config.responseType === "arraybuffer") return false;
  return true;
};

// When a list response comes back online, also synthesize a URL cache entry
// for each item under '/<collection>/:id'. That way a follow-up
// apiClient.get('/<collection>/:id') (e.g. edit page) hits the cache without
// requiring the user to have visited that detail page online first.
const mirrorListItems = async (collection, items, idKey = "id") => {
  await Promise.all(
    items.map((item) => {
      const id = item?.[idKey] || item?._id;
      if (!id) return null;
      return putResponse(`/${collection}/${id}`, item);
    })
  );
};

// Some endpoints have known shapes we want to mirror into entity stores so
// later UI code can read them without re-parsing the URL-keyed response cache.
const mirrorToEntityStores = async (url, data) => {
  try {
    if (/^\/gigs(\?|$)/.test(url) && Array.isArray(data)) {
      // Gigs use `gig_id` as their canonical id — `/gigs/:id` URL param is
      // the gig_id value (see GigManager.jsx). The events store has
      // keyPath:"id" so we synthesize an id field from gig_id.
      const items = data
        .filter((g) => g && (g.gig_id || g.id || g._id))
        .map((g) => ({ ...g, id: g.gig_id || g.id || g._id }));
      await putEvents(items);
      await Promise.all(
        items.map((g) => {
          const key = g.gig_id || g.id;
          return key ? putResponse(`/gigs/${key}`, g) : null;
        })
      );
    } else if (/^\/bands(\?|$)/.test(url) && Array.isArray(data)) {
      // Bands use `band_id` as the canonical id (per existing PUT/GET routes).
      await mirrorListItems("bands", data, "band_id");
    } else if (/^\/collaborators(\?|$)/.test(url) && Array.isArray(data)) {
      await mirrorListItems("collaborators", data, "collaborator_id");
    } else if (/^\/venues(\?|$)/.test(url) && Array.isArray(data)) {
      // Venues are addressed by `name` in the URL ('/venues/:name') per the
      // existing UI; fall back to id if name missing.
      await Promise.all(
        data.map((v) => {
          if (!v) return null;
          const key = v.name || v.venue_id || v.id;
          return key ? putResponse(`/venues/${encodeURIComponent(key)}`, v) : null;
        })
      );
    } else if (/^\/auth\/me$/.test(url) && data && typeof data === "object") {
      await putProfile(data);
    }
  } catch {
    // Mirroring is best-effort; never let it break the response flow.
  }
};

export const attachOfflineInterceptors = (apiClient) => {
  // Response interceptor — write-through and cache-fallback.
  apiClient.interceptors.response.use(
    async (response) => {
      const config = response.config || {};
      if (isGet(config)) {
        // Always mirror /auth/me to profile store (even though we skip the URL cache).
        if (/^\/auth\/me$/.test(config.url || "")) {
          await mirrorToEntityStores("/auth/me", response.data);
        } else if (isCacheable(config)) {
          await putResponse(config.url, response.data);
          await mirrorToEntityStores(config.url || "", response.data);
        }
      }
      return response;
    },
    async (error) => {
      const config = error?.config || {};
      // Only attempt cache fallback for GETs that failed at the network level.
      // A 4xx/5xx with a response body is a real server answer — don't paper over it.
      const isNetworkFail = !error?.response;
      if (isNetworkFail && isGet(config) && isCacheable(config)) {
        const cached = await getResponse(config.url);
        if (cached) {
          return {
            data: cached.data,
            status: 200,
            statusText: "OK (from offline cache)",
            headers: {},
            config,
            fromCache: true,
            cachedAt: cached.cachedAt,
          };
        }
      }
      return Promise.reject(error);
    }
  );
};
